import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Collection, 
  Card, 
  Heading, 
  Text, 
  View, 
  SelectField,
  Loader
} from '@aws-amplify/ui-react';
import { API } from 'aws-amplify';
import { FaArrowUp, FaTimes } from 'react-icons/fa';
import "../js/loader.js";
import "../js/appConstants.js";

function FileList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sortBySongNumber, setSortBySongNumber] = useState(false);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileSearch, setFileSearch] = useState('');
  const [songSearch, setSongSearch] = useState('');
  const [languageAccordionOpen, setLanguageAccordionOpen] = useState(false);
  const [searchAccordionOpen, setSearchAccordionOpen] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [playlistMenuOpen, setPlaylistMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  // Helper function to format file name (remove number prefix and .txt suffix)
  const formatFileName = (fileName) => {
    return fileName.replace(/^\d+[\s_-]*/, '').replace(/\.txt$/, '');
  };
  
  // Export the formatFileName function to make it available to other components
  window.formatFileName = formatFileName;
  
  // Set up window load event and call immediately if already loaded
  useEffect(() => {
    const handleLoad = () => {
      window.clickLog("load", "homepage");            
    };
    
    // If document is already loaded, call the function immediately
    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
    }
    
    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  // Fetch all files on component mount
  useEffect(() => {
    fetchFiles();
  }, []);

  // Handle back navigation from FileViewer
  useEffect(() => {
    if (location.state?.backFromFile) {
      const { folder, language, fileName } = location.state;
      setSelectedFolder(folder);
      setSelectedLanguage(language);
      setSongSearch(fileName.replace(/^\d+[\s_-]*/, '').replace(/\.txt$/, ''));
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Process files when they change
  useEffect(() => {
    if (files.length > 0) {
      // Extract unique folders
      const uniqueFolders = [...new Set(files.map(file => file.folder))];
      setFolders(uniqueFolders);
      
      // Find folders that have Sanskrit files
      const sanskritFolders = [...new Set(
        files.filter(file => file.language === 'SAN')
          .map(file => file.folder)
      )];
      
      // Set default folder - prefer Sanskrit folders if available
      if (!selectedFolder && uniqueFolders.length > 0) {
        if (sanskritFolders.length > 0) {
          setSelectedFolder(sanskritFolders[0]);
        } else {
          setSelectedFolder(uniqueFolders[0]);
        }
      }
    }
  }, [files, selectedFolder]);

  // Update languages when folder changes
  useEffect(() => {
    if (selectedFolder && files.length > 0) {
      // Get files for the selected folder
      const folderFiles = files.filter(file => file.folder === selectedFolder);
      
      // Extract unique languages
      const uniqueLanguages = [...new Set(folderFiles.map(file => file.language))];
      setLanguages(uniqueLanguages);
      
      // Check if Sanskrit is available for this folder
      const hasSanskrit = uniqueLanguages.includes('SAN');
      
      // Set default language - prefer Sanskrit if available
      const languageExists = uniqueLanguages.includes(selectedLanguage);
      if ((!selectedLanguage || !languageExists) && uniqueLanguages.length > 0) {
        if (hasSanskrit) {
          setSelectedLanguage('SAN');
        } else {
          setSelectedLanguage(uniqueLanguages[0]);
        }
      }
    }
  }, [selectedFolder, files]);

  // Fetch files from API
  const fetchFiles = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get the current authenticated session
      let authHeaders = {};
      // Call the API with correct path
      const response = await API.get('NamapagesAPI', 'files', {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response && (Array.isArray(response) || response.files)) {
        // Handle new response structure with files property
        const filesData = response.files || response;
        
        // Check for playlists in the response
        if (response.playlists && Array.isArray(response.playlists)) {
          setPlaylists(response.playlists);
          console.log('Playlists loaded:', response.playlists.length);
        }
        
        // Log the first file to see if s3_path and song_no are included
        // if (filesData.length > 0) {
        //   console.log('Sample file data:', filesData[0]);
        //   console.log('Has song_no field:', filesData[0].hasOwnProperty('song_no'));
        // }
        
        setFiles(filesData);
        console.log('Files loaded successfully:', filesData.length);
      } else {
        console.error('Invalid response format:', response);
        setError('Received invalid data format from server.');
        
        // Fallback to mock data if API fails
        const mockData = [
          { folder: "Thirumurai", language: "TAM", file_name: "01_Thevaram.txt", file_path: "Thirumurai/TAM/01_Thevaram.txt", song_no: 1 },
          { folder: "Thirumurai", language: "TAM", file_name: "02_Thiruvasagam.txt", file_path: "Thirumurai/TAM/02_Thiruvasagam.txt", song_no: 2 },
          { folder: "Thirumurai", language: "ENG", file_name: "01_Thevaram.txt", file_path: "Thirumurai/ENG/01_Thevaram.txt", song_no: 3 },
          { folder: "Thirumurai", language: "ENG", file_name: "02_Thiruvasagam.txt", file_path: "Thirumurai/ENG/02_Thiruvasagam.txt", song_no: 4 },
          { folder: "Divyaprabandham", language: "TAM", file_name: "01_Nalayira.txt", file_path: "Divyaprabandham/TAM/01_Nalayira.txt", song_no: 5 },
          { folder: "Divyaprabandham", language: "ENG", file_name: "01_Nalayira.txt", file_path: "Divyaprabandham/ENG/01_Nalayira.txt", song_no: 6 },
          { folder: "Bhagavad Gita", language: "SAN", file_name: "01_Chapter1.txt", file_path: "Bhagavad Gita/SAN/01_Chapter1.txt", song_no: 7 },
          { folder: "Bhagavad Gita", language: "ENG", file_name: "01_Chapter1.txt", file_path: "Bhagavad Gita/ENG/01_Chapter1.txt", song_no: 8 },
          { folder: "Upanishads", language: "SAN", file_name: "01_Isha.txt", file_path: "Upanishads/SAN/01_Isha.txt", song_no: 9 },
          { folder: "Upanishads", language: "ENG", file_name: "01_Isha.txt", file_path: "Upanishads/ENG/01_Isha.txt", song_no: 10 },
          { folder: "Puranas", language: "SAN", file_name: "01_Vishnu.txt", file_path: "Puranas/SAN/01_Vishnu.txt", song_no: 11 },
          { folder: "Puranas", language: "ENG", file_name: "01_Vishnu.txt", file_path: "Puranas/ENG/01_Vishnu.txt", song_no: 12 }
        ];
        
        setFiles(mockData);
        console.log('Falling back to mock data');
      }
    } catch (err) {
      console.error('Error loading files:', err);
      // Compose error message for HTML response
      const errorMessage = [
        'Please come back later or try again after some time.  ', 
        'नामा परते साधन नाहीं'        
      ].join(' ');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Get files for the selected folder and language
  const getFilteredFiles = () => {
    if (!selectedFolder || !selectedLanguage) return [];
    
    // console.log('Filtering files:', {
    //   allFiles: files,
    //   selectedFolder,
    //   selectedLanguage
    // });
    
    const filtered = files.filter(
      file => file.folder === selectedFolder && file.language === selectedLanguage
    );
    
    // Sort files by filename
    filtered.sort((a, b) => {
      // Remove numeric prefix and .txt suffix for sorting
      const nameA = formatFileName(a.file_name);
      const nameB = formatFileName(b.file_name);
      return nameA.localeCompare(nameB);
    });
    
    //console.log('Filtered files:', filtered);
    return filtered;
  };

  // Search files by song number or name across all files
  const searchSongFiles = () => {
    if (!songSearch.trim()) return [];
    
    const searchTerm = songSearch.toLowerCase();
    return files.filter(file => {
      // Check song_no field (exact match or starts with for numbers)
      const songNo = file.song_no ? file.song_no.toString() : '';
      const songNoMatch = songNo === searchTerm || songNo.startsWith(searchTerm);
      
      // Check file name
      const fileName = formatFileName(file.file_name || file.name || '').toLowerCase();
      const fileNameMatch = fileName.includes(searchTerm);
      
      // Also check if search term is numeric and matches file name prefix
      const isNumericSearch = /^\d+$/.test(searchTerm);
      const fileNamePrefix = file.file_name ? file.file_name.match(/^(\d+)/)?.[1] : '';
      const prefixMatch = isNumericSearch && fileNamePrefix && (fileNamePrefix === searchTerm || fileNamePrefix.startsWith(searchTerm));
      
      return songNoMatch || fileNameMatch || prefixMatch;
    });
  };

  // State for search inputs
  const [folderSearch, setFolderSearch] = useState('');
  const [languageSearch, setLanguageSearch] = useState('');
  
  // Format folder name for display (capitalize first letter, replace underscores with spaces)
  const formatFolderName = (folder) => {
    return folder
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };
  
  // Filter folders based on search
  const filteredFolders = folderSearch 
    ? folders.filter(folder => 
        folder.toLowerCase().replace(/_/g, ' ').includes(folderSearch.toLowerCase())
      )
    : folders;
  
  // Language code mapping
  const languageCodeMap = {
    'ENG': 'English',
    'TAM': 'Tamil',
    'IAST': 'English (IAST)',
    'SAN': 'Sanskrit',
    'MIX': 'Mixed',
    'DEV': 'Devanagari',
    'ROM': 'Roman'
  };
  
  // Get display name for a language
  const getLanguageDisplay = (lang) => {
    // If the language code is directly in our map
    if (languageCodeMap[lang]) {
      return `${languageCodeMap[lang]} - ${lang}`;
    }
    
    // Check if it's a longer string containing the code
    for (const [code, name] of Object.entries(languageCodeMap)) {
      if (lang.includes(code)) {
        return `${name} - ${code}`;
      }
    }
    
    // Fallback to just the language name
    return lang;
  };
  
  // Filter languages based on search
  const filteredLanguages = languages.filter(language => 
    language.toLowerCase().includes(languageSearch.toLowerCase()) || 
    getLanguageDisplay(language).toLowerCase().includes(languageSearch.toLowerCase())
  );
  
  // Handle folder change
  const handleFolderChange = (e) => {
    const newFolder = e.target.value;
    console.log('Folder changed to:', newFolder);
    setSelectedFolder(newFolder);
    
    // Find available languages for this folder
    const folderFiles = files.filter(file => file.folder === newFolder);
    const availableLanguages = [...new Set(folderFiles.map(file => file.language))];
    
    // Check if Sanskrit is available for this folder
    const hasSanskrit = availableLanguages.includes('SAN');
    
    // Set language - prefer Sanskrit if available
    if (availableLanguages.length > 0) {
      if (hasSanskrit) {
        setSelectedLanguage('SAN');
      } else {
        setSelectedLanguage(availableLanguages[0]);
      }
    } else {
      setSelectedLanguage('');
    }
  };

  // Handle language change
  const handleLanguageChange = (e) => {
    const newLanguage = e.target.value;
    console.log('Language changed to:', newLanguage);
    setSelectedLanguage(newLanguage);
  };

  // Toggle playlist menu
  const togglePlaylistMenu = () => {
    setPlaylistMenuOpen(!playlistMenuOpen);
  };
  
  // Handle playlist selection
  const handlePlaylistSelect = (playlist) => {
    console.log('Selected playlist:', playlist);
    setPlaylistMenuOpen(false);
    window.clickLog('Selected playlist:', playlist, "homepage")
    
    // Navigate to playlist content viewer
    navigate(`/playlist/${playlist.id}`, { 
      state: { 
        playlist: playlist 
      }
    });
  };

  // Scroll-to-top button logic
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 200);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return <Loader size="large" />;
  }

  if (error) {
    return <Text variation="error">{error}</Text>;
  }

  // Helper function to highlight matched text
  const highlightMatchedText = (text, query) => {
    if (!query) return text;
    
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const startIndex = lowerText.indexOf(lowerQuery);
    
    if (startIndex === -1) return text;
    
    const endIndex = startIndex + lowerQuery.length;
    const beforeMatch = text.substring(0, startIndex);
    const match = text.substring(startIndex, endIndex);
    const afterMatch = text.substring(endIndex);
    
    return (
      <>
        {beforeMatch}
        <span style={{ color: '#4CAF50' }}>{match}</span>
        {afterMatch}
      </>
    );
  };
  
  // Get filtered files based on song search or regular folder/language filter
  let filteredFiles = songSearch.trim() 
    ? searchSongFiles().filter(file => {
        const displayName = formatFileName(file.file_name || file.name || '');
        return fileSearch ? displayName.toLowerCase().includes(fileSearch.toLowerCase()) : true;
      })
    : getFilteredFiles().filter(file => {
        const displayName = formatFileName(file.file_name);
        return displayName.toLowerCase().includes(fileSearch.toLowerCase());
      });
      
  // Sort files by song number if enabled
  if (sortBySongNumber) {
    filteredFiles = [...filteredFiles].sort((a, b) => {
      // Convert song numbers to integers for proper numeric comparison
      // Use null check and isNaN to handle all edge cases
      const songNoA = a.song_no !== undefined && a.song_no !== null ? 
        (isNaN(parseInt(a.song_no)) ? Number.MAX_SAFE_INTEGER : parseInt(a.song_no)) : 
        Number.MAX_SAFE_INTEGER;
      
      const songNoB = b.song_no !== undefined && b.song_no !== null ? 
        (isNaN(parseInt(b.song_no)) ? Number.MAX_SAFE_INTEGER : parseInt(b.song_no)) : 
        Number.MAX_SAFE_INTEGER;
      
      // Apply sort direction
      return sortDirection === 'asc' ? songNoA - songNoB : songNoB - songNoA;
    });
  }

  // Helper: Get song counts for each language in the selected folder
  const getLanguageSongCounts = () => {
    if (!selectedFolder) return [];
    // Get all files in the selected folder
    const folderFiles = files.filter(file => file.folder === selectedFolder);
    // Count by language
    const counts = {};
    folderFiles.forEach(file => {
      const lang = file.language;
      if (!counts[lang]) counts[lang] = 0;
      counts[lang]++;
    });
    // Return as array of { lang, count }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1]) // sort by count desc
      .map(([lang, count]) => ({ lang, count }));
  };

  return (
    <View>
      {/* Hamburger menu icon (aligned right) */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginBottom: '1rem',
        position: 'relative'
      }}>
        {/* Playlist menu icon */}
        <button 
          onClick={togglePlaylistMenu}
          style={{
            background: '#EFEBE9',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
          aria-label="Show playlists"
        >
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="#795548" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
          
          {/* Playlist dropdown menu */}
          {playlistMenuOpen && (
            <div style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              backgroundColor: 'white',
              border: '1px solid #D7CCC8',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(121, 85, 72, 0.15)',
              zIndex: 100,
              minWidth: '250px',
              maxHeight: '400px',
              overflowY: 'auto'
            }}>
              <div style={{ 
                padding: '0.75rem', 
                borderBottom: '1px solid #eee',
                backgroundColor: '#EFEBE9',
                color: '#5D4037',
                fontWeight: 'bold'
              }}>
                <h3 style={{ margin: 0, fontSize: '1rem' }}>Playlists</h3>
              </div>
              
              {playlists.length > 0 ? (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {playlists.map((playlist, index) => (
                    <li 
                      key={playlist.id}
                      style={{ 
                        padding: '0.75rem', 
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        backgroundColor: index % 2 === 0 ? '#F5F5F5' : 'white'
                      }}
                      onClick={() => handlePlaylistSelect(playlist)}
                    >
                      <div style={{ fontWeight: 'bold' }}>{playlist.name}</div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>ID: {playlist.id}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
                  No playlists available
                </div>
              )}
            </div>
          )}
        </div>

      {/* Song Search Section */}
      <div style={{
        backgroundColor: '#E3F2FD',
        padding: '1.5rem',
        borderRadius: '8px',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        position: 'relative'
      }}>
        <label 
          htmlFor="songSearch" 
          style={{ 
            display: 'block', 
            marginBottom: '0.75rem',
            fontWeight: 'bold',
            color: '#333'
          }}
        >
          <span style={{ marginRight: '8px', color: '#1976D2' }}>🎵</span>
          Search by Song Number or Name
        </label>
        <div style={{ position: 'relative' }}>
          <input
            id="songSearch"
            type="text"
            value={songSearch}
            onChange={(e) => {
              const searchValue = e.target.value;
              setSongSearch(searchValue);
              // Auto-select folder when song is found
              if (searchValue.trim()) {
                const searchTerm = searchValue.toLowerCase();
                const foundFiles = files.filter(file => {
                  const songNo = file.song_no ? file.song_no.toString() : '';
                  const songNoMatch = songNo === searchTerm || songNo.startsWith(searchTerm);
                  const fileName = formatFileName(file.file_name || file.name || '').toLowerCase();
                  const fileNameMatch = fileName.includes(searchTerm);
                  const isNumericSearch = /^\d+$/.test(searchTerm);
                  const fileNamePrefix = file.file_name ? file.file_name.match(/^(\d+)/)?.[1] : '';
                  const prefixMatch = isNumericSearch && fileNamePrefix && (fileNamePrefix === searchTerm || fileNamePrefix.startsWith(searchTerm));
                  return songNoMatch || fileNameMatch || prefixMatch;
                });
                if (foundFiles.length > 0) {
                  setSelectedFolder(foundFiles[0].folder);
                }
              }
            }}
            placeholder="Type song number or name to search"
            style={{ 
              width: '100%', 
              padding: '0.75rem',
              borderRadius: '4px',
              border: '1px solid #ddd',
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
              fontSize: '1rem',
              paddingRight: songSearch ? '2.5rem' : undefined
            }}
          />
          {songSearch && (
            <button
              onClick={() => setSongSearch('')}
              style={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(33,150,243,0.12)',
                border: 'none',
                borderRadius: '50%',
                width: '2rem',
                height: '2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                transition: 'background 0.2s',
                color: '#1976D2',
                opacity: 0.85
              }}
              aria-label="Clear search"
              tabIndex={0}
            >
              <FaTimes style={{ fontSize: '1.1rem' }} />
            </button>
          )}
        </div>
      </div>
      
      <div style={{ 
        display: 'flex', 
        gap: '20px', 
        marginBottom: '2rem',
        flexWrap: 'wrap'
      }}>        
        <div style={{ 
          flex: '1', 
          minWidth: '250px',
          backgroundColor: '#EFEBE9',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <label 
            htmlFor="folderSearch" 
            style={{ 
              display: 'block', 
              marginBottom: '0.75rem',
              fontWeight: 'bold',
              color: '#333'
            }}
          >
            <span style={{ marginRight: '8px', color: '#4CAF50' }}>📁</span>
            Select
          </label>
          <input
            id="folderSearch"
            type="text"
            value={folderSearch}
            onChange={(e) => {
              const searchValue = e.target.value;
              setFolderSearch(searchValue);
              // Auto-select the first filtered folder when typing
              if (filteredFolders.length > 0 && searchValue) {
                const matchedFolders = folders.filter(folder => 
                  folder.toLowerCase().replace(/_/g, ' ').includes(searchValue.toLowerCase())
                );
                if (matchedFolders.length > 0) {
                  setSelectedFolder(matchedFolders[0]);
                }
              }
            }}
            placeholder="Type to search folders"
            style={{ 
              width: '100%', 
              padding: '0.75rem',
              marginBottom: '0.75rem',
              borderRadius: '4px',
              border: '1px solid #ddd',
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
              fontSize: '1rem'
            }}
          />
          <select
            value={selectedFolder}
            onChange={handleFolderChange}
            style={{ 
              width: '100%', 
              padding: '0.75rem',
              borderRadius: '4px',
              border: '1px solid #ddd',
              backgroundColor: '#f8f9fa',
              fontSize: '1rem',
              cursor: 'pointer'
            }}
          >
            {filteredFolders.map(folder => (
              <option key={folder} value={folder}>{formatFolderName(folder)}</option>
            ))}
          </select>
        </div>

        {/* Language status label */}
        <div style={{
          backgroundColor: '#E8F5E9',
          padding: '0.5rem 1rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          color: '#2E7D32',
          fontSize: '0.9rem',
          fontWeight: 'bold'
        }}>
          <span style={{ marginRight: '8px' }}>📋</span>
          {languageCodeMap[selectedLanguage] || selectedLanguage} files | Change language ↓
        </div>
        
        {/* Language Accordion */}
        <div style={{ 
          flex: '1', 
          minWidth: '250px',
          backgroundColor: '#EFEBE9',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <div 
            onClick={() => setLanguageAccordionOpen(!languageAccordionOpen)}
            style={{
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <label 
              style={{ 
                display: 'block',
                fontWeight: 'bold',
                color: '#333',
                margin: 0,
                cursor: 'pointer'
              }}
            >
              <span style={{ marginRight: '8px', color: '#2196F3' }}>🔤</span>
              Language
            </label>
            <span style={{ fontSize: '1.2rem' }}>
              {languageAccordionOpen ? '▲' : '▼'}
            </span>
          </div>
          
          {languageAccordionOpen && (
            <div style={{ marginTop: '0.75rem' }}>
              <input
                id="languageSearch"
                type="text"
                value={languageSearch}
                onChange={(e) => setLanguageSearch(e.target.value)}
                placeholder="Type to search languages"
                disabled={!selectedFolder}
                style={{ 
                  width: '100%', 
                  padding: '0.75rem',
                  marginBottom: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                  fontSize: '1rem',
                  opacity: !selectedFolder ? 0.6 : 1
                }}
              />
              <select
                value={selectedLanguage}
                onChange={handleLanguageChange}
                disabled={!selectedFolder}
                style={{ 
                  width: '100%', 
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  backgroundColor: '#f8f9fa',
                  fontSize: '1rem',
                  cursor: selectedFolder ? 'pointer' : 'not-allowed',
                  opacity: !selectedFolder ? 0.6 : 1
                }}
              >
                {filteredLanguages.map(language => (
                  <option key={language} value={language}>{getLanguageDisplay(language)}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>
      
      {/* Search Files Accordion */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{
          backgroundColor: '#EFEBE9',
          padding: '1rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <div 
            onClick={() => setSearchAccordionOpen(!searchAccordionOpen)}
            style={{
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <label 
              htmlFor="fileSearch" 
              style={{ 
                display: 'block',
                fontWeight: 'bold',
                color: '#333',
                margin: 0,
                cursor: 'pointer'
              }}
            >
              <span style={{ marginRight: '8px', color: '#FF9800' }}>🔍</span>
              Search Files
            </label>
            <span style={{ fontSize: '1.2rem' }}>
              {searchAccordionOpen ? '▲' : '▼'}
            </span>
          </div>
          
          {searchAccordionOpen && (
            <input
              id="fileSearch"
              type="text"
              value={fileSearch}
              onChange={(e) => setFileSearch(e.target.value)}
              placeholder="Type to search files"
              style={{ 
                width: '100%', 
                padding: '0.75rem',
                marginTop: '0.75rem',
                borderRadius: '4px',
                border: '1px solid #ddd',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                fontSize: '1rem'
              }}
            />
          )}
        </div>
      </div>
      
      {/* Sort by Song Number Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={() => {
              if (sortBySongNumber) {
                // If already sorting, toggle direction
                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
              } else {
                // Enable sorting
                setSortBySongNumber(true);
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              padding: '6px 12px',
              backgroundColor: sortBySongNumber ? '#EFEBE9' : 'white',
              border: '1px solid #D7CCC8',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              color: '#5D4037',
              fontWeight: sortBySongNumber ? 'bold' : 'normal'
            }}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              style={{ 
                transform: sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s'
              }}
            >
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <polyline points="19 12 12 19 5 12"></polyline>
            </svg>
            Sort by Song Number
            {sortBySongNumber && (
              <span style={{ marginLeft: '5px', fontSize: '0.8rem' }}>({sortDirection === 'asc' ? 'Low to High' : 'High to Low'})</span>
            )}
          </button>
          {sortBySongNumber && (
            <button
              onClick={() => setSortBySongNumber(false)}
              style={{
                marginLeft: '5px',
                padding: '6px',
                backgroundColor: 'white',
                border: '1px solid #D7CCC8',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                color: '#5D4037'
              }}
              title="Clear sorting"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      
      {/* Language Song Counts - show as one or two rows above the song tiles */}
      {getLanguageSongCounts().length > 0 && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem 1rem',
          margin: '0 0 1rem 0',
          justifyContent: 'flex-start',
          alignItems: 'center',
          fontSize: '0.98rem'
        }}>
          <span style={{
            fontWeight: 'bold',
            color: '#5D4037', // dark brown
            marginRight: '0.5rem'
          }}>
            Song Counts by Language:
          </span>
          {getLanguageSongCounts().map(({ lang, count }) => (
            <span key={lang} style={{
              background: lang === selectedLanguage ? '#E3F2FD' : '#F5F5F5',
              color: lang === selectedLanguage ? '#1976D2' : '#555',
              borderRadius: '8px',
              padding: '2px 10px',
              fontWeight: lang === selectedLanguage ? 'bold' : 'normal',
              border: lang === selectedLanguage ? '1px solid #90CAF9' : '1px solid #eee',
              marginBottom: '2px',
              whiteSpace: 'nowrap'
            }}>
              {count} {languageCodeMap[lang] || lang}
            </span>
          ))}
        </div>
      )}

      {filteredFiles.length > 0 ? (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: '0.5rem'
        }}>
          {filteredFiles.map(file => {
            const fileName = file.file_name || file.name || '';
            const displayName = formatFileName(fileName);
            const songNo = file.song_no;
            
            return (
              <Card 
                key={file.file_path || `${file.folder}-${file.language}-${fileName}`} 
                padding="0.5rem"
                borderRadius="8px"
                boxShadow="0 2px 8px rgba(0,0,0,0.08)"
                backgroundColor="#fff"
                style={{ 
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  cursor: 'pointer'
                }}
                _hover={{
                  transform: 'translateY(-4px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                }}
              >
                <Link 
                  to={`/file/${file.folder}/${file.language}/${encodeURIComponent(fileName)}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                  onClick={() => window.clickLog("file_click - " +  file.folder + "/" + file.language + "/" + fileName, "homepage")}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ 
                      fontSize: '1rem', 
                      marginRight: '0.4rem',
                      color: '#4CAF50',
                      flexShrink: 0
                    }}>
                      📄
                    </span>
                    <div style={{ overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                        <span style={{ 
                          backgroundColor: '#E3F2FD', 
                          color: '#1976D2', 
                          padding: '2px 5px', 
                          borderRadius: '8px', 
                          fontSize: '0.65rem', 
                          fontWeight: 'bold',
                          flexShrink: 0,
                          whiteSpace: 'nowrap'
                        }}>
                          Song No: {songNo || 'N/A'}
                        </span>
                        <Heading level={5} style={{ margin: 0, fontSize: '0.75rem', display: 'inline' }}>
                          {(songSearch && (() => {
                            const searchTerm = songSearch.toLowerCase();
                            const songNoMatch = songNo && (songNo.toString() === searchTerm || songNo.toString().startsWith(searchTerm));
                            const fileNameMatch = displayName.toLowerCase().includes(searchTerm);
                            const isNumericSearch = /^\d+$/.test(searchTerm);
                            const fileNamePrefix = fileName ? fileName.match(/^(\d+)/)?.[1] : '';
                            const prefixMatch = isNumericSearch && fileNamePrefix && (fileNamePrefix === searchTerm || fileNamePrefix.startsWith(searchTerm));
                            return songNoMatch || fileNameMatch || prefixMatch;
                          })()) 
                            ? highlightMatchedText(displayName, songSearch)
                            : (fileSearch && displayName.toLowerCase().includes(fileSearch.toLowerCase()))
                            ? highlightMatchedText(displayName, fileSearch)
                            : displayName
                          }
                        </Heading>
                        <span style={{ 
                          fontSize: '0.65rem', 
                          color: '#666', 
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          ({languageCodeMap[file.language] || file.language})
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </Card>
            );
          })}
        </div>
      ) : (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          color: '#666'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📂</div>
          <Text>
            {songSearch ? 
              `No songs matching "${songSearch}" found.` :
              fileSearch ? 
              `No files matching "${fileSearch}" found in ${formatFolderName(selectedFolder)}/${selectedLanguage}.` : 
              `No files found for the selected folder and language.`
            }
          </Text>
        </div>
      )}
      
      {/* Floating Scroll-to-Top Button */}
      {showScrollTop && (
        <button
          onClick={handleScrollToTop}
          style={{
            position: 'fixed',
            bottom: '2.5rem',
            right: '2rem', // moved slightly to the left
            zIndex: 1000,
            background: 'rgba(33, 150, 243, 0.75)',
            border: 'none',
            borderRadius: '50%',
            width: '36px', // reduced by 25%
            height: '36px', // reduced by 25%
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
          aria-label="Scroll to top"
        >
          <FaArrowUp style={{ color: 'white', fontSize: '1.125rem', opacity: 0.95, transform: 'scale(0.75)' }} />
        </button>
      )}
    </View>
  );
}

export default FileList;